<script setup>
// NOTICE: KAPTION-INTEGRATION
// Aggregate Campaign Reports page. Fetches pre-aggregated analytics from the
// mock endpoint (GET /campaigns/reports) on mount and on every filter change,
// and feeds it to the presentational KaptionReportsDashboard. Falls back to
// local mock data if the endpoint is unavailable, so the page renders even
// without the analytics worker / Rails backend. Gated behind KAPTION_CAMPAIGN_REPORTS.
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';

import CampaignsAPI from 'dashboard/api/campaigns';
import KaptionReportsDashboard from 'dashboard/components-next/Campaigns/Pages/KaptionReportsDashboard.vue';
import {
  mockTotals,
  mockTimelineDaily,
  mockTimeline30Days,
  mockTimelineMonthly,
  mockTopCampaigns,
  mockInboxes,
  mockFilterCampaigns,
} from 'dashboard/components-next/Campaigns/__mocks__/reportsData';

const router = useRouter();
const store = useStore();
const { isKaptionFlagEnabled, refreshFlags } = useKaptionFeatureFlags();
// NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
const { canUseFeature } = useFeaturePermissions();

const totals = ref({});
const timeline = ref([]);
const topCampaigns = ref([]);
const loading = ref(true);

// --- Filter source lists (real data from the store, mock fallback for demos) ---
const storeInboxes = useMapGetter('inboxes/getInboxes');
const storeCampaigns = useMapGetter('campaigns/getAllCampaigns');

const inboxes = computed(() => {
  const list = storeInboxes.value || [];
  if (!list.length) return mockInboxes;
  return list.map(inbox => ({
    id: inbox.id,
    name: inbox.name,
    phone_number: inbox.phone_number,
  }));
});

const filterCampaigns = computed(() => {
  const list = storeCampaigns.value || [];
  if (!list.length) return mockFilterCampaigns;
  return list.map(campaign => ({
    id: campaign.id,
    title: campaign.title,
    campaign_type: campaign.campaign_type,
  }));
});

// Local mock fallback used when the API is unavailable.
const fallbackTimeline = granularity => {
  if (granularity === 'month') return mockTimelineMonthly;
  if (granularity === 'week') return mockTimeline30Days;
  return mockTimelineDaily;
};

const toIsoDate = date => date.toISOString().slice(0, 10);

const buildOptions = (type, filters) => {
  // 'all' = all-time: omit the date window so the worker aggregates full history.
  // kaption-campaign-reports.DASHBOARD.2, kaption-campaign-reports.DASHBOARD.4
  const isAllTime = filters.days === 'all';
  const days = Number(filters.days || 7);
  const end = new Date();
  const start = new Date(end.getTime() - days * 24 * 60 * 60 * 1000);
  return {
    type,
    campaignType: filters.type === 'all' ? undefined : filters.type,
    granularity: filters.granularity,
    inboxId: filters.inboxId === 'all' ? undefined : filters.inboxId,
    campaignIds: filters.campaignIds?.length ? filters.campaignIds : undefined,
    startDate: isAllTime ? undefined : toIsoDate(start),
    endDate: isAllTime ? undefined : toIsoDate(end),
    // Default ON; only sent as false when the user unticks "Include archived".
    includeArchived: filters.includeArchived === false ? false : undefined,
  };
};

const fetchAll = async (filters = {}) => {
  loading.value = true;
  // Normalize the dashboard's `type` filter ('campaigns' → 'one_off') for the API.
  const apiFilters = {
    ...filters,
    type: filters.type === 'campaigns' ? 'one_off' : filters.type,
  };
  const granularity = filters.granularity || 'day';
  try {
    const [totalsRes, timelineRes, campaignsRes] = await Promise.all([
      CampaignsAPI.getReportsData(buildOptions('totals', apiFilters)),
      CampaignsAPI.getReportsData(buildOptions('timeline', apiFilters)),
      CampaignsAPI.getReportsData(buildOptions('top_campaigns', apiFilters)),
    ]);
    totals.value = totalsRes.data ?? mockTotals;
    timeline.value = timelineRes.data ?? fallbackTimeline(granularity);
    topCampaigns.value = campaignsRes.data ?? mockTopCampaigns;
  } catch (error) {
    // Backend / analytics worker unavailable — render with local mock data so
    // the dashboard still works (mock-only mode, per the integration plan).
    // eslint-disable-next-line no-console
    console.warn(
      '[KaptionReports] reports endpoint unavailable, using mock data:',
      error?.message
    );
    totals.value = mockTotals;
    timeline.value = fallbackTimeline(granularity);
    topCampaigns.value = mockTopCampaigns;
  } finally {
    loading.value = false;
  }
};

const onFilterChange = filters => fetchAll(filters);

const onSelectCampaign = id => {
  router.push({
    name: 'campaigns_whatsapp_report',
    params: { campaignId: id },
  });
};

// Lazy-resolve a campaign's WhatsApp template for the leaderboard preview popover.
// The leaderboard rows carry only metrics, so fetch the campaign on demand and use
// its `resolved_template` (falling back to matching the inbox's synced WABA
// templates by name + language). Cached per campaign; null → "unavailable" state.
const templateCache = new Map();
const resolveTemplate = async id => {
  if (templateCache.has(id)) return templateCache.get(id);
  let template = null;
  try {
    const { data } = await CampaignsAPI.show(id);
    template = data?.resolved_template || data?.template || null;
    if (!template && data?.template_params?.name) {
      const inboxId = data.inbox?.id ?? data.inbox_id;
      const getter = store.getters['inboxes/getWhatsAppTemplates'];
      const templates =
        typeof getter === 'function' ? getter(inboxId) || [] : [];
      const { name, language } = data.template_params;
      template =
        templates.find(
          tpl => tpl.name === name && (!language || tpl.language === language)
        ) || null;
    }
  } catch (error) {
    template = null;
  }
  templateCache.set(id, template);
  return template;
};

onMounted(async () => {
  // Ensure flags are loaded before gating (stale-while-revalidate can return
  // false synchronously on first navigation).
  try {
    await refreshFlags();
  } catch {
    // ignore — fall through to the cached value
  }
  // feature-permissions.REPORTS.1
  if (
    !isKaptionFlagEnabled('KAPTION_CAMPAIGN_REPORTS') ||
    !canUseFeature('campaign_reports')
  ) {
    router.replace({ name: 'campaigns_whatsapp_index' });
    return;
  }
  store.dispatch('inboxes/get');
  store.dispatch('campaigns/get');
  fetchAll({ type: 'all', granularity: 'day', days: '7' });
});
</script>

<template>
  <div class="-mx-6">
    <KaptionReportsDashboard
      :totals="totals"
      :timeline="timeline"
      :campaigns="topCampaigns"
      :inboxes="inboxes"
      :filter-campaigns="filterCampaigns"
      :loading="loading"
      :resolve-template="resolveTemplate"
      @filter-change="onFilterChange"
      @select-campaign="onSelectCampaign"
    />
  </div>
</template>
